import React, {useContext, useEffect, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {saveSlideClicksData} from '../service/analytics.service';
import {currentSlideIndexSelector, loadedPresentationSelector, visibleSlideSelector} from '../store/selectors/presentation.selectors';
import {runFileIdSelector} from '../store/selectors/runfile.selectors';
import {SlideRefContext} from '../context/SlideRefContext';

export function useSlideClickAnalytics(): (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void {
    const loadedPresentation = useSelector(loadedPresentationSelector);
    const runFileId = useSelector(runFileIdSelector);
    const currentSlideIndex = useSelector(currentSlideIndexSelector);
    const slides = useSelector(visibleSlideSelector);
    const {slideRef} = useContext(SlideRefContext);

    const currentSlideId = useMemo(() => {
        return slides ? slides[currentSlideIndex]?._id : '';
    }, [currentSlideIndex, slides]);

    const slideClicks = useRef<Parameters<typeof saveSlideClicksData>[0]>({
        slideId: '',
        runFileId: '',
        clicks: [],
    });

    useEffect(() => {
        if (slideClicks.current.clicks.length > 0) {
            saveSlideClicksData(slideClicks.current);
            slideClicks.current.clicks = [];
        }
    }, [currentSlideIndex]);

    useEffect(() => {
        slideClicks.current.slideId = currentSlideId;
        slideClicks.current.runFileId = runFileId || '';
        slideClicks.current.clicks = [];
    }, [currentSlideId, runFileId]);

    useEffect(() => {
        const buffer = slideClicks;
        const interval = setInterval(() => {
            if (buffer.current.clicks.length > 0) {
                saveSlideClicksData(buffer.current);
                buffer.current.clicks = [];
            }
        }, 5000);
        return () => {
            clearInterval(interval);
            if (buffer.current.clicks.length > 0) {
                saveSlideClicksData(buffer.current);
                buffer.current.clicks = [];
            }
        };
    }, []);

    function handleSlideClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
        if (!loadedPresentation?.dataCollection) return;
        if (!slideRef.current) return;

        const h = slideRef.current.clientHeight;
        const w = slideRef.current.clientWidth;

        const rect = slideRef.current.getBoundingClientRect();
        let x = e.clientX - rect.x;
        let y = e.clientY - rect.y;

        x = Math.floor((x / w) * 100);
        y = Math.floor((y / h) * 100);

        slideClicks.current.clicks.push({x, y});
    }

    return handleSlideClick;
}
